/**
 * The store document (D64–D67) as the storefront reads it: the owner's listing
 * and product templates, header and footer, and product card. Pure functions,
 * so templates and settings are testable without rendering.
 */
import { migrateStore, defaultChrome, STORE_PAGES } from '@dreamtree-org/landing-kit/schema';

/**
 * The store document behind an appearance response. New servers send `doc`;
 * an older one sends only the landing `page`, which migrates to a v2 document
 * with the default templates. Nothing loaded yet: the defaults.
 */
export function storeDocOf(appearance) {
  if (appearance && appearance.doc && appearance.doc.version === 2) return appearance.doc;
  return migrateStore(appearance ? appearance.page : null);
}

/** A template page with every setting filled (a doc saved before a setting existed gets its default). */
export function templateOf(doc, pageKey) {
  const def = STORE_PAGES[pageKey];
  const page = (doc && doc.pages && doc.pages[pageKey]) || {};
  const defaults = Object.fromEntries(def.settings.map((f) => [f.key, f.default]));
  return {
    template: page.template || def.default,
    settings: { ...defaults, ...(def.templates[page.template || def.default] || {}).settings, ...page.settings },
    blocks: Array.isArray(page.blocks) ? page.blocks : [],
  };
}

/** True when block `b` is shown and comes straight after block `a`, so `a` can lay both out side by side. */
export function follows(blocks, a, b) {
  const shown = blocks.filter((x) => x && x.enabled !== false);
  const i = shown.findIndex((x) => x.type === a);
  return i >= 0 && shown[i + 1] && shown[i + 1].type === b;
}

// Tailwind needs whole class names in the source, so grids are a lookup, not string building.
const GRID_MOBILE = { 1: 'grid-cols-1', 2: 'grid-cols-2' };
// Beside a 260px sidebar a desktop row fits one card fewer until the screen is wide.
const GRID_DESKTOP = {
  sidebar: { 3: 'sm:grid-cols-2 xl:grid-cols-3', 4: 'sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' },
  wide: { 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-3 lg:grid-cols-4' },
};

/** Grid classes for the results, from the listing settings. */
export function resultsGrid(settings) {
  const mobile = GRID_MOBILE[settings.columns_mobile] || GRID_MOBILE[2];
  const desktop = (settings.filters === 'sidebar' ? GRID_DESKTOP.sidebar : GRID_DESKTOP.wide)[settings.columns] || GRID_DESKTOP.wide[3];
  return `grid ${mobile} ${desktop} gap-3 sm:gap-4`;
}

/** A sort from the URL wins; otherwise the owner's default sort (when the catalog knows it). */
export function effectiveSort(urlSort, defaultSort, sorts) {
  if (urlSort) return urlSort;
  return sorts.some((s) => s.value === defaultSort) ? defaultSort : 'relevance';
}

/**
 * URL params for a filter state, keeping `sort` only when it differs from the
 * page's default (so the default sort is a clean URL and "Relevance" still sticks).
 */
export function withSortParam(params, sort, defaultSort) {
  const out = { ...params };
  delete out.sort;
  if (sort && sort !== defaultSort) out.sort = sort;
  return out;
}

/** The slice of results to show: one page, or every page so far with "Load more". */
export function pageWindow(total, page, size, paging) {
  const pages = Math.max(1, Math.ceil(total / size));
  const p = Math.min(Math.max(1, page), pages);
  return paging === 'load_more' ? { start: 0, end: Math.min(total, p * size), page: p, pages, more: p < pages } : { start: (p - 1) * size, end: Math.min(total, p * size), page: p, pages, more: false };
}

const RATIO = { '4:3': 'aspect-[4/3]', '1:1': 'aspect-square', '3:4': 'aspect-[3/4]' };
// A row card is square on phones and takes the owner's shape from `sm`.
const RATIO_ROW = { '4:3': 'aspect-square sm:aspect-[4/3]', '1:1': 'aspect-square', '3:4': 'aspect-square sm:aspect-[3/4]' };
/** Photo-shape classes for a card setting (`row`: the phone-horizontal card). */
export const ratioClass = (ratio, row = false) => (row ? RATIO_ROW : RATIO)[ratio] || (row ? RATIO_ROW : RATIO)['4:3'];

/** A listing's card photos: the cover first, then the rest (signed URLs from the server). */
export function cardPhotos(l) {
  const urls = [l.cover_url, ...(Array.isArray(l.image_urls) ? l.image_urls : [])].filter(Boolean);
  return [...new Set(urls)];
}

/** Header/footer with every field present (older docs, or none loaded yet). */
export function chromeOf(doc) {
  const c = (doc && doc.chrome) || {};
  const d = defaultChrome();
  return {
    announcement: { ...d.announcement, ...c.announcement },
    header: { ...d.header, ...c.header },
    footer: { ...d.footer, ...c.footer },
  };
}

/** True for links that leave the storefront (rendered as <a>, not router links). */
export const isExternal = (href) => /^(https?:)?\/\//i.test(String(href || '')) || /^(mailto|tel):/i.test(String(href || ''));
